import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SunIcon,
  CloudIcon,
  ChartBarIcon,
  ShoppingBagIcon,
  ArrowsRightLeftIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { weatherAPI, destinationsAPI } from '../services/api';
import { loadCustomDestinations, CustomDestination } from '../utils/customDestinations';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { useSettingsStore } from '../store/settingsStore';

// Tab definitions
type WeatherTab = 'current' | 'forecast' | 'historical' | 'packing' | 'compare';

interface TabConfig {
  id: WeatherTab;
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  description: string;
}

const TABS: TabConfig[] = [
  { id: 'current', name: 'Current Weather', icon: SunIcon, description: 'Real-time conditions for any destination' },
  { id: 'forecast', name: 'Trip Forecast', icon: CloudIcon, description: 'Weather outlook for your travel dates' },
  { id: 'historical', name: 'Climate Guide', icon: ChartBarIcon, description: 'Monthly averages and best times to visit' },
  { id: 'packing', name: 'Packing List', icon: ShoppingBagIcon, description: 'AI-powered packing recommendations' },
  { id: 'compare', name: 'Compare Cities', icon: ArrowsRightLeftIcon, description: 'Side-by-side weather comparison' },
];

const ACTIVITY_OPTIONS = [
  'hiking', 'beach', 'business', 'sightseeing', 'nightlife', 'adventure', 'skiing', 'photography',
];

// Weather icon mapping to emoji for visual display
const WEATHER_ICONS: Record<string, string> = {
  sunny: '☀️',
  partly_cloudy: '⛅',
  cloudy: '☁️',
  fog: '🌫️',
  drizzle: '🌦️',
  rain: '🌧️',
  snow: '❄️',
  thunderstorm: '⛈️',
  unknown: '🌡️',
};

const AQI_COLORS: Record<string, string> = {
  'Good': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'Moderate': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  'Unhealthy for Sensitive Groups': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  'Unhealthy': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  'Very Unhealthy': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  'Hazardous': 'bg-red-200 text-red-900 dark:bg-red-900/50 dark:text-red-200',
};

export const WeatherIntelligence: React.FC = () => {
  const { t } = useTranslation(['weather', 'common']);
  const tempUnit = useSettingsStore((s) => s.getEffectiveTemperatureUnit)();
  const isFahrenheit = tempUnit === 'fahrenheit';

  // Temperature display helper: shows primary unit large, secondary small
  const formatTemp = (c: number, f: number) =>
    isFahrenheit ? `${f}°F` : `${c}°C`;
  const formatTempDual = (c: number, f: number) =>
    isFahrenheit
      ? <>{f}°F <span className="text-lg text-gray-500">/ {c}°C</span></>
      : <>{c}°C <span className="text-lg text-gray-500">/ {f}°F</span></>;
  const formatTempSmall = (c: number, f: number) =>
    isFahrenheit ? `${f}°F / ${c}°C` : `${c}°C / ${f}°F`;

  const [activeTab, setActiveTab] = useState<WeatherTab>('current');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Current Weather tab
  const [currentLocation, setCurrentLocation] = useState('');
  const [currentResult, setCurrentResult] = useState<any>(null);
  const [currentSearchResults, setCurrentSearchResults] = useState<any[]>([]);
  const [currentSearchOpen, setCurrentSearchOpen] = useState(false);
  const currentSearchRef = useRef<HTMLDivElement>(null);

  // Forecast tab
  const [forecastLocation, setForecastLocation] = useState('');
  const [forecastDays, setForecastDays] = useState(7);
  const [forecastResult, setForecastResult] = useState<any>(null);
  const [forecastSearchResults, setForecastSearchResults] = useState<any[]>([]);
  const [forecastSearchOpen, setForecastSearchOpen] = useState(false);
  const forecastSearchRef = useRef<HTMLDivElement>(null);

  // Historical tab
  const [historicalLocation, setHistoricalLocation] = useState('');
  const [historicalResult, setHistoricalResult] = useState<any>(null);
  const [historicalSearchResults, setHistoricalSearchResults] = useState<any[]>([]);
  const [historicalSearchOpen, setHistoricalSearchOpen] = useState(false);
  const historicalSearchRef = useRef<HTMLDivElement>(null);

  // Packing tab
  const [packingDestination, setPackingDestination] = useState('');
  const [packingDuration, setPackingDuration] = useState('7');
  const [packingActivities, setPackingActivities] = useState<string[]>([]);
  const [packingStartDate, setPackingStartDate] = useState('');
  const [packingResult, setPackingResult] = useState<any>(null);
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem('auxilio-travel-packing-checks') || '{}');
    } catch { return {}; }
  });
  const [packingSearchResults, setPackingSearchResults] = useState<any[]>([]);
  const [packingSearchOpen, setPackingSearchOpen] = useState(false);
  const packingSearchRef = useRef<HTMLDivElement>(null);

  // Compare tab
  const [compareLocations, setCompareLocations] = useState<string[]>(['', '']);
  const [compareResult, setCompareResult] = useState<any>(null);

  // Custom destinations
  const [customDestinations, setCustomDestinations] = useState<CustomDestination[]>([]);

  useEffect(() => {
    setCustomDestinations(loadCustomDestinations());
  }, []);

  // Save packing checks to localStorage
  useEffect(() => {
    localStorage.setItem('auxilio-travel-packing-checks', JSON.stringify(checkedItems));
  }, [checkedItems]);

  // Close search dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (currentSearchRef.current && !currentSearchRef.current.contains(e.target as Node)) setCurrentSearchOpen(false);
      if (forecastSearchRef.current && !forecastSearchRef.current.contains(e.target as Node)) setForecastSearchOpen(false);
      if (historicalSearchRef.current && !historicalSearchRef.current.contains(e.target as Node)) setHistoricalSearchOpen(false);
      if (packingSearchRef.current && !packingSearchRef.current.contains(e.target as Node)) setPackingSearchOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced destination search helper
  const useDestinationSearch = (
    query: string,
    setResults: React.Dispatch<React.SetStateAction<any[]>>,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    focused: boolean,
  ) => {
    useEffect(() => {
      if (!query.trim() || !focused) {
        setResults([]);
        return;
      }
      const timer = setTimeout(async () => {
        try {
          const customMatches = customDestinations
            .filter(cd => cd.city.toLowerCase().includes(query.toLowerCase()) || cd.country.toLowerCase().includes(query.toLowerCase()))
            .map(cd => ({ id: cd.id, city: cd.city, country: cd.country, is_custom: true }));
          const data = await destinationsAPI.search({ q: query, limit: 6 });
          const apiResults = (data.results || []).map((r: any) => ({ ...r, is_custom: false }));
          const merged = [...apiResults, ...customMatches].slice(0, 8);
          setResults(merged);
          setOpen(merged.length > 0);
        } catch {
          setResults([]);
        }
      }, 300);
      return () => clearTimeout(timer);
    }, [query, customDestinations, focused]);
  };

  // Search hooks for each tab
  const [currentFocused, setCurrentFocused] = useState(false);
  const [forecastFocused, setForecastFocused] = useState(false);
  const [historicalFocused, setHistoricalFocused] = useState(false);
  const [packingFocused, setPackingFocused] = useState(false);

  useDestinationSearch(currentLocation, setCurrentSearchResults, setCurrentSearchOpen, currentFocused);
  useDestinationSearch(forecastLocation, setForecastSearchResults, setForecastSearchOpen, forecastFocused);
  useDestinationSearch(historicalLocation, setHistoricalSearchResults, setHistoricalSearchOpen, historicalFocused);
  useDestinationSearch(packingDestination, setPackingSearchResults, setPackingSearchOpen, packingFocused);

  // ---- API Handlers ----

  const handleCurrentWeather = async () => {
    if (!currentLocation.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await weatherAPI.getCurrentWeather(currentLocation.trim());
      setCurrentResult(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch current weather');
    } finally {
      setLoading(false);
    }
  };

  const handleForecast = async () => {
    if (!forecastLocation.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await weatherAPI.getForecast(forecastLocation.trim(), { days: forecastDays });
      setForecastResult(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch forecast');
    } finally {
      setLoading(false);
    }
  };

  const handleHistorical = async () => {
    if (!historicalLocation.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await weatherAPI.getHistorical(historicalLocation.trim());
      setHistoricalResult(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch climate data');
    } finally {
      setLoading(false);
    }
  };

  const handlePackingList = async () => {
    if (!packingDestination.trim()) return;
    setLoading(true);
    setError(null);
    setCheckedItems({});
    try {
      const result = await weatherAPI.generatePackingList({
        destination: packingDestination.trim(),
        duration_days: parseInt(packingDuration) || 7,
        activities: packingActivities,
        start_date: packingStartDate || undefined,
      });
      setPackingResult(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate packing list');
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    const locs = compareLocations.filter(l => l.trim());
    if (locs.length < 2) {
      setError('Please enter at least 2 cities to compare');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await weatherAPI.compareWeather({ locations: locs });
      setCompareResult(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to compare weather');
    } finally {
      setLoading(false);
    }
  };

  // ---- Search Dropdown Component ----

  const renderSearchDropdown = (
    results: any[],
    isOpen: boolean,
    onSelect: (city: string) => void,
    ref: React.RefObject<HTMLDivElement>,
  ) => {
    if (!isOpen || results.length === 0) return null;
    return (
      <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
        {results.map((r, i) => (
          <button
            key={i}
            className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 dark:hover:bg-cyan-900/30 text-gray-800 dark:text-gray-200"
            onClick={() => {
              onSelect(r.city);
              setCurrentSearchOpen(false);
              setForecastSearchOpen(false);
              setHistoricalSearchOpen(false);
              setPackingSearchOpen(false);
            }}
          >
            <span className="font-medium">{r.city}</span>
            <span className="text-gray-500 dark:text-gray-400">, {r.country}</span>
            {r.is_custom && <span className="ml-2 text-xs text-cyan-600 dark:text-cyan-400">(custom)</span>}
          </button>
        ))}
      </div>
    );
  };

  // ---- Location Input Component ----

  const renderLocationInput = (
    value: string,
    setValue: React.Dispatch<React.SetStateAction<string>>,
    searchResults: any[],
    searchOpen: boolean,
    searchRef: React.RefObject<HTMLDivElement>,
    setFocused: React.Dispatch<React.SetStateAction<boolean>>,
    onSubmit: () => void,
    buttonLabel: string,
  ) => (
    <div className="flex gap-3 items-end">
      <div className="flex-1 relative" ref={searchRef}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('weather:labels.location')}
        </label>
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 200)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
            placeholder={t('weather:labels.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-cyan-500 dark:focus:border-cyan-400"
          />
        </div>
        {renderSearchDropdown(searchResults, searchOpen, (city) => setValue(city), searchRef)}
      </div>
      <button
        onClick={onSubmit}
        disabled={!value.trim() || loading}
        className="px-5 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors"
      >
        {buttonLabel}
      </button>
    </div>
  );

  // ---- Tab Renderers ----

  const renderCurrentWeather = () => (
    <div className="space-y-6">
      {renderLocationInput(currentLocation, setCurrentLocation, currentSearchResults, currentSearchOpen, currentSearchRef as any, setCurrentFocused, handleCurrentWeather, t('weather:labels.getWeather'))}

      {currentResult && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{currentResult.city}, {currentResult.country}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{new Date(currentResult.timestamp).toLocaleString()}</p>
            </div>
            <span className="text-4xl">{WEATHER_ICONS[currentResult.weather_icon] || '🌡️'}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Temperature */}
            <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-4">
              <p className="text-sm text-cyan-700 dark:text-cyan-300 font-medium">{t('weather:labels.temperature')}</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{formatTempDual(currentResult.temperature_c, currentResult.temperature_f)}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('weather:labels.feelsLike')}: {formatTempSmall(currentResult.apparent_temperature_c, currentResult.apparent_temperature_f)}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{currentResult.weather_description}</p>
            </div>

            {/* Wind & Humidity */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">{t('weather:labels.wind')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{currentResult.wind_speed_kmh} km/h <span className="text-sm font-normal">({currentResult.wind_speed_mph} mph)</span></p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{currentResult.wind_direction_cardinal} ({currentResult.wind_direction_degrees}°)</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{t('weather:labels.humidity')}: {currentResult.humidity_pct}%</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('weather:labels.precipitation')}: {currentResult.precipitation_mm} mm</p>
            </div>

            {/* UV & Air Quality */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              {currentResult.uv_index !== null && currentResult.uv_index !== undefined && (
                <div className="mb-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">{t('weather:labels.uvIndex')}</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{currentResult.uv_index}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {currentResult.uv_index <= 2 ? 'Low' : currentResult.uv_index <= 5 ? 'Moderate' : currentResult.uv_index <= 7 ? 'High' : 'Very High'}
                  </p>
                </div>
              )}
              {currentResult.air_quality_label && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">{t('weather:labels.airQuality')}</p>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${AQI_COLORS[currentResult.air_quality_label] || 'bg-gray-100 text-gray-800'}`}>
                    {currentResult.air_quality_label} (AQI: {currentResult.air_quality_index})
                  </span>
                  {currentResult.pm2_5 !== null && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">PM2.5: {currentResult.pm2_5} | PM10: {currentResult.pm10}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderForecast = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 items-end">
        <div className="flex-1 relative" ref={forecastSearchRef}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('weather:labels.location')}</label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={forecastLocation}
              onChange={(e) => setForecastLocation(e.target.value)}
              onFocus={() => setForecastFocused(true)}
              onBlur={() => setTimeout(() => setForecastFocused(false), 200)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleForecast(); }}
              placeholder={t('weather:labels.searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-cyan-500"
            />
          </div>
          {renderSearchDropdown(forecastSearchResults, forecastSearchOpen, (city) => setForecastLocation(city), forecastSearchRef)}
        </div>
        <div className="w-32">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('weather:labels.forecastDays')}</label>
          <select
            value={forecastDays}
            onChange={(e) => setForecastDays(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            {[3, 5, 7, 10, 14, 16].map(d => <option key={d} value={d}>{d} days</option>)}
          </select>
        </div>
        <button
          onClick={handleForecast}
          disabled={!forecastLocation.trim() || loading}
          className="px-5 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors"
        >
          {t('weather:labels.getForecast')}
        </button>
      </div>

      {forecastResult && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {forecastResult.city}, {forecastResult.country} — {forecastResult.forecast_days}-day forecast
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {forecastResult.daily_forecasts.map((day: any, i: number) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-center">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{day.day_of_week}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{day.date}</p>
                <p className="text-2xl my-2">{WEATHER_ICONS[day.weather_icon] || '🌡️'}</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">{day.weather_description}</p>
                <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {isFahrenheit
                    ? <>{day.temp_high_f}° <span className="text-gray-400 font-normal">/</span> {day.temp_low_f}°F</>
                    : <>{day.temp_high_c}° <span className="text-gray-400 font-normal">/</span> {day.temp_low_c}°C</>}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isFahrenheit ? `${day.temp_high_c}° / ${day.temp_low_c}°C` : `${day.temp_high_f}° / ${day.temp_low_f}°F`}
                </p>
                {day.precipitation_probability_pct !== null && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">🌧 {day.precipitation_probability_pct}%</p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">💨 {day.wind_speed_max_kmh} km/h</p>
              </div>
            ))}
          </div>

          {forecastResult.summary && (
            <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-cyan-800 dark:text-cyan-300 mb-1">{t('weather:forecast.aiSummary')}</h4>
              <p className="text-sm text-cyan-700 dark:text-cyan-200">{forecastResult.summary}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderHistorical = () => {
    const maxTemp = historicalResult ? Math.max(...historicalResult.monthly_averages.map((m: any) => isFahrenheit ? m.avg_high_f : m.avg_high_c)) : 40;
    const maxRain = historicalResult ? Math.max(...historicalResult.monthly_averages.map((m: any) => m.precipitation_mm)) : 200;

    return (
      <div className="space-y-6">
        {renderLocationInput(historicalLocation, setHistoricalLocation, historicalSearchResults, historicalSearchOpen, historicalSearchRef as any, setHistoricalFocused, handleHistorical, t('weather:labels.getClimateData'))}

        {historicalResult && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {historicalResult.city}, {historicalResult.country}
              </h3>
              {historicalResult.climate_type && (
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('weather:labels.climateType')}: {historicalResult.climate_type}</span>
              )}
            </div>

            {/* Best months */}
            {historicalResult.best_months.length > 0 && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <p className="text-sm font-semibold text-green-800 dark:text-green-300">{t('weather:labels.bestMonths')}</p>
                <p className="text-sm text-green-700 dark:text-green-200 mt-1">
                  {historicalResult.best_months.join(', ')}
                  {historicalResult.best_months_reason && ` — ${historicalResult.best_months_reason}`}
                </p>
              </div>
            )}

            {/* Seasonal warnings */}
            {historicalResult.seasonal_warnings.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  {t('weather:labels.seasonalWarnings')}
                </p>
                <ul className="text-sm text-amber-700 dark:text-amber-200 mt-1 list-disc list-inside">
                  {historicalResult.seasonal_warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {/* Temperature bars */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase">{t('weather:labels.temperature')} ({isFahrenheit ? '°F' : '°C'})</h4>
              <div className="space-y-2">
                {historicalResult.monthly_averages.map((m: any, i: number) => {
                  const isBest = historicalResult.best_months.includes(m.month.substring(0, 3));
                  const highVal = isFahrenheit ? m.avg_high_f : m.avg_high_c;
                  const lowVal = isFahrenheit ? m.avg_low_f : m.avg_low_c;
                  const highPct = Math.max(5, (highVal / maxTemp) * 100);
                  const lowPct = Math.max(3, (Math.max(0, lowVal) / maxTemp) * 100);
                  return (
                    <div key={i} className={`flex items-center gap-2 py-1 px-2 rounded ${isBest ? 'bg-green-50 dark:bg-green-900/20' : ''}`}>
                      <span className="w-8 text-xs font-medium text-gray-600 dark:text-gray-400">{m.month.substring(0, 3)}</span>
                      <div className="flex-1 flex items-center gap-1">
                        <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-4 relative overflow-hidden">
                          <div className="absolute left-0 top-0 h-full bg-red-300 dark:bg-red-500/60 rounded-full" style={{ width: `${highPct}%` }} />
                          <div className="absolute left-0 top-0 h-full bg-blue-300 dark:bg-blue-500/60 rounded-full" style={{ width: `${lowPct}%` }} />
                        </div>
                      </div>
                      <span className="w-20 text-xs text-right text-gray-700 dark:text-gray-300">{lowVal}° / {highVal}°</span>
                      <span className="w-16 text-xs text-right text-blue-600 dark:text-blue-400">{m.precipitation_mm}mm</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-300 dark:bg-red-500/60 rounded" /> High</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-300 dark:bg-blue-500/60 rounded" /> Low</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-100 dark:bg-green-900/30 rounded" /> Best month</span>
              </div>
            </div>

            {/* Monthly table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-400">Month</th>
                    <th className="text-right px-3 py-2 text-gray-600 dark:text-gray-400">{t('weather:labels.high')}</th>
                    <th className="text-right px-3 py-2 text-gray-600 dark:text-gray-400">{t('weather:labels.low')}</th>
                    <th className="text-right px-3 py-2 text-gray-600 dark:text-gray-400">{t('weather:labels.rain')}</th>
                    <th className="text-right px-3 py-2 text-gray-600 dark:text-gray-400">{t('weather:labels.rainyDays')}</th>
                  </tr>
                </thead>
                <tbody>
                  {historicalResult.monthly_averages.map((m: any, i: number) => {
                    const isBest = historicalResult.best_months.includes(m.month.substring(0, 3));
                    return (
                      <tr key={i} className={`border-t border-gray-100 dark:border-gray-700 ${isBest ? 'bg-green-50 dark:bg-green-900/20' : ''}`}>
                        <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{m.month}</td>
                        <td className="text-right px-3 py-2 text-gray-700 dark:text-gray-300">{formatTempSmall(m.avg_high_c, m.avg_high_f)}</td>
                        <td className="text-right px-3 py-2 text-gray-700 dark:text-gray-300">{formatTempSmall(m.avg_low_c, m.avg_low_f)}</td>
                        <td className="text-right px-3 py-2 text-blue-600 dark:text-blue-400">{m.precipitation_mm} mm</td>
                        <td className="text-right px-3 py-2 text-gray-600 dark:text-gray-400">{m.rainy_days}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPacking = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="relative" ref={packingSearchRef}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('weather:labels.location')}</label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={packingDestination}
              onChange={(e) => setPackingDestination(e.target.value)}
              onFocus={() => setPackingFocused(true)}
              onBlur={() => setTimeout(() => setPackingFocused(false), 200)}
              placeholder={t('weather:labels.searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-cyan-500"
            />
          </div>
          {renderSearchDropdown(packingSearchResults, packingSearchOpen, (city) => setPackingDestination(city), packingSearchRef)}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('weather:labels.tripDuration')}</label>
          <input
            type="number"
            min={1}
            max={90}
            value={packingDuration}
            onChange={(e) => setPackingDuration(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-cyan-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('weather:labels.startDate')}</label>
        <input
          type="date"
          value={packingStartDate}
          onChange={(e) => setPackingStartDate(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-cyan-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('weather:labels.activities')}</label>
        <div className="flex flex-wrap gap-2">
          {ACTIVITY_OPTIONS.map(act => (
            <button
              key={act}
              onClick={() => setPackingActivities(prev => prev.includes(act) ? prev.filter(a => a !== act) : [...prev, act])}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                packingActivities.includes(act)
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t(`weather:activities.${act}`, act)}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={handlePackingList}
        disabled={!packingDestination.trim() || loading}
        className="px-6 py-2.5 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors"
      >
        {t('weather:labels.generatePackingList')}
      </button>

      {packingResult && (
        <div className="space-y-4">
          <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-3">
            <p className="text-sm text-cyan-700 dark:text-cyan-200"><strong>{t('weather:packing.weatherContext')}:</strong> {packingResult.weather_summary}</p>
          </div>

          {packingResult.layering_advice && (
            <p className="text-sm text-gray-700 dark:text-gray-300"><strong>{t('weather:packing.layeringAdvice')}:</strong> {packingResult.layering_advice}</p>
          )}
          {packingResult.cultural_notes && (
            <p className="text-sm text-gray-700 dark:text-gray-300"><strong>{t('weather:packing.culturalNotes')}:</strong> {packingResult.cultural_notes}</p>
          )}

          <div className="flex gap-3 text-sm">
            {packingResult.rain_gear_needed && (
              <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">🌧 {t('weather:packing.rainGear')}</span>
            )}
            {packingResult.sun_protection_needed && (
              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">☀️ {t('weather:packing.sunProtection')}</span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {packingResult.packing_categories.map((cat: any, ci: number) => (
              <div key={ci} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{cat.category}</h4>
                {cat.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{cat.notes}</p>}
                <ul className="space-y-1">
                  {cat.items.map((item: string, ii: number) => {
                    const key = `${cat.category}-${ii}`;
                    return (
                      <li key={ii} className="flex items-center gap-2">
                        <button
                          onClick={() => setCheckedItems(prev => ({ ...prev, [key]: !prev[key] }))}
                          className="flex-shrink-0"
                        >
                          {checkedItems[key] ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                          )}
                        </button>
                        <span className={`text-sm ${checkedItems[key] ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                          {item}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderCompare = () => (
    <div className="space-y-6">
      <div className="space-y-3">
        {compareLocations.map((loc, i) => (
          <div key={i} className="flex gap-2 items-center">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-6">{i + 1}.</span>
            <input
              type="text"
              value={loc}
              onChange={(e) => {
                const updated = [...compareLocations];
                updated[i] = e.target.value;
                setCompareLocations(updated);
              }}
              placeholder={`City ${i + 1}`}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-cyan-500"
            />
            {i >= 2 && (
              <button onClick={() => setCompareLocations(prev => prev.filter((_, j) => j !== i))}>
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-red-500" />
              </button>
            )}
          </div>
        ))}
        {compareLocations.length < 4 && (
          <button
            onClick={() => setCompareLocations(prev => [...prev, ''])}
            className="text-sm text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 font-medium"
          >
            + {t('weather:labels.addCity')}
          </button>
        )}
      </div>

      <button
        onClick={handleCompare}
        disabled={compareLocations.filter(l => l.trim()).length < 2 || loading}
        className="px-6 py-2.5 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors"
      >
        {t('weather:labels.compareWeather')}
      </button>

      {compareResult && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {compareResult.locations.map((loc: any, i: number) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-center">
                <h4 className="font-bold text-gray-900 dark:text-gray-100">{loc.city}</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">{loc.country}</p>
                <p className="text-3xl my-3">{WEATHER_ICONS[loc.weather_icon] || '🌡️'}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatTemp(loc.temperature_c, loc.temperature_f)}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{isFahrenheit ? `${loc.temperature_c}°C` : `${loc.temperature_f}°F`}</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{loc.weather_description}</p>
                <div className="mt-3 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                  <p>💧 {loc.humidity_pct}%</p>
                  <p>💨 {loc.wind_speed_kmh} km/h</p>
                  {loc.uv_index !== null && <p>☀️ UV {loc.uv_index}</p>}
                </div>
              </div>
            ))}
          </div>

          {compareResult.recommendation && (
            <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-cyan-800 dark:text-cyan-300 mb-1">{t('weather:compare.aiRecommendation')}</h4>
              <p className="text-sm text-cyan-700 dark:text-cyan-200">{compareResult.recommendation}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ---- Main Render ----

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'current': return renderCurrentWeather();
      case 'forecast': return renderForecast();
      case 'historical': return renderHistorical();
      case 'packing': return renderPacking();
      case 'compare': return renderCompare();
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <CloudIcon className="h-7 w-7 text-cyan-500" />
          {t('weather:title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">{t('weather:subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto gap-1 mb-6 border-b border-gray-200 dark:border-gray-700 pb-px">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setError(null); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border-b-2 border-cyan-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            <span className="hidden sm:inline">{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Tab Content */}
      {renderActiveTab()}

      {/* Disclaimer */}
      <div className="mt-8 text-xs text-gray-400 dark:text-gray-500">
        <p>{t('common:disclaimer.travel')}</p>
        <p className="mt-1">Weather data provided by <a href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:text-cyan-600">Open-Meteo</a> (free, open-source weather API).</p>
      </div>

      <LoadingOverlay isVisible={loading} message="Fetching weather data..." subMessage="This may take a moment" />
    </div>
  );
};
